"use client";

import "@styles/global.css";

import { ReactNode, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { NextPageWithLayoutAndPageType, pageAccessType } from "types/base";
import { ConfigPage, WebConfig } from "types/config";

import { generateOrganizationSchema } from "@/utils/schemas";
import { generateStructuredData } from "@/utils/seo-utils";
import { setAxiosAuthInterceptor } from "@picovico/helpers";
import { HTMLHeadRenderer } from "@picovico/ui";
import { wrapper } from "@store/store";
import withSiteConfig from "hoc/withSiteConfig";
import AuthHandler from "layout/AuthHandler";
import AppLayout from "layout/Layout";
import RouteProgressBar from "@components/RouteProgressBar";
import { NextPage } from "next";
import { AppProps } from "next/app";
import Head from "next/head";
import { Provider } from "react-redux";
import { loadCineSettingAsync } from "store/slices/cineSettingSlice";
import { WebThemeProvider } from "theme";
import { apiRoutes } from "../routes/apiRoutes";

setAxiosAuthInterceptor({ apiRoutes, source: "web" });

export type AppPageProps = AppProps & {
  Component: NextPageWithLayoutAndPageType;
};

const AppWithThemeProvider = ({
  pageAccess,
  pageType,
  children,
  serverConfig,
}: {
  pageAccess: pageAccessType | undefined;
  pageType: string;
  children: ReactNode;
  serverConfig?: WebConfig & {
    configInitialized: boolean;
    configLoadFail: boolean;
  };
}) => {
  const storeConfig = useAppSelector((state) => state.config);
  const config = storeConfig.configInitialized
    ? storeConfig
    : serverConfig || storeConfig;
  const siteUrl = config?.website_url;
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useAppSelector((state) => state.user);
  const canAnonView =
    config?.pages?.find((page: ConfigPage) => page?.page_type === pageType)
      ?.config?.anon_view || pageAccess;

  useEffect(() => {
    if (isLoggedIn) dispatch(loadCineSettingAsync());
  }, [dispatch, isLoggedIn]);

  const overscrollBgColor =
    (config?.layout_config?.background as any) || "#ffffff";

  // Paint the document background to match the tenant theme so any area not
  // covered by page content (overscroll rubber-band, edges) isn't white on a
  // dark-themed site.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.backgroundColor = overscrollBgColor;
    document.body.style.backgroundColor = overscrollBgColor;
  }, [overscrollBgColor]);

  return (
    <>
      <Head>
        <title style={{ color: overscrollBgColor }}>
          {config?.company_info?.name}
        </title>
        <meta
          name="keywords"
          content={`${config?.company_info?.name} Hall Website CinemaHall`}
          key="keywords"
        />
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <meta name="author" content={`${config?.company_info?.name} `} />
        <meta
          name="description"
          content={`${config?.company_info?.description} `}
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content={config?.company_info?.name}
          key="og:title"
        />
        <meta
          property="og:description"
          content={config?.company_info?.description}
          key="og:description"
        />
        <meta property="og:image" content={config?.logo} key="og:image" />
        <meta
          property="og:site_name"
          content={config?.company_info?.name}
          key="og:site_name"
        />

        <meta name="robots" content="index, follow" />
        <meta property="og:url" content={siteUrl} />
        <link rel="canonical" href={siteUrl} />
        <meta name="theme-color" content={overscrollBgColor} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config?.company_info?.name} />
        <meta
          name="twitter:description"
          content={config?.company_info?.description}
        />
        <meta name="twitter:image" content={config?.logo} />

        <meta
          name="application-name"
          content={`${config.company_info?.name}`}
        />
        {/* Native-app / installed PWA feel on mobile */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="default"
        />
        <meta
          name="apple-mobile-web-app-title"
          content={`${config.company_info?.name || ""}`}
        />
        {/* {config.logo && <link rel="apple-touch-icon" href={config.logo} />}
        <link rel="icon" type="image/x-icon" href={config.logo} />
        <link rel="icon" type="image/png" href={config.logo} /> */}
        {config?.head_tags &&
          HTMLHeadRenderer({ htmlElement: config.head_tags })}
        {config.pwa_config && (
          <link rel="manifest" href="/manifest.webmanifest" />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: generateStructuredData(
              generateOrganizationSchema({
                name: config?.company_info?.name || "",
                logo: config.logo,
                url: siteUrl,
                description: config.company_info?.description,
              }),
            ),
          }}
        />
      </Head>
      <WebThemeProvider theme={config.site_theme as Record<string, unknown>}>
        <RouteProgressBar />
        <AuthHandler>
          <AppLayout pageAccess={canAnonView}>{children}</AppLayout>
        </AuthHandler>
      </WebThemeProvider>
    </>
  );
};

const getPageWithConfig = (
  Component: NextPageWithLayoutAndPageType,
  props: Pick<AppPageProps, "pageProps">,
) => {
  const Page = withSiteConfig(Component);

  return <Page {...props} />;
};

function App({ Component, pageProps, ...rest }: AppPageProps) {
  const { store } = wrapper.useWrappedStore(rest);
  const getPageLayout = pageProps?.getLayout || ((page: NextPage) => page);
  return (
    <Provider store={store}>
      <AppWithThemeProvider
        pageAccess={Component.access}
        pageType={Component?.page_type || ""}
        serverConfig={pageProps?.config}
      >
        {Component.page_type
          ? getPageWithConfig(Component, pageProps)
          : getPageLayout(<Component {...pageProps} />)}
      </AppWithThemeProvider>
    </Provider>
  );
}

export default App;
